// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { toArraySchema } from '@/api/schemas';
import { caseInsensitiveLowerEnum } from '@/constants/case-insensitive-enum';
import { shippingCarrierSchema } from '@/constants/shipping-carrier';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { DateTime } from 'luxon';
import { z } from 'zod';

// The channels a message type can be delivered on. This is deliberately not
// `@/constants/notification-channel`, which lists only the channels the email
// digest settings draw a column for.
export const messageChannelSchema = caseInsensitiveLowerEnum([
  'in_app',
  'email',
  'slack',
]);

export type MessageChannel = z.infer<typeof messageChannelSchema>;

const MESSAGE_CHANNEL_LABELS: Record<MessageChannel, string> = {
  in_app: 'In-app',
  email: 'Email',
  slack: 'Slack',
};

/** Returns the column heading for one delivery channel. */
export function toMessageChannelLabel(channel: MessageChannel): string {
  return MESSAGE_CHANNEL_LABELS[channel];
}

// A message is stored as a template and the facts it was written from, and the
// screen draws it. A template is data, never executed: new wording ships a new
// template and leaves every message already written saying what it said.
//
// The context is typed JSON. A key is either plain text or one of the
// well-known attributes below, and which one it is comes from the message
// type's own schema rather than a tag on the wire.
// Record attributes keep their stored display fields, independent of live responses.
const locationReferenceSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

const projectReferenceSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

const customerReferenceSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

const shipmentReferenceSchema = z.object({
  id: z.string(),
  key: z.string(),
  shipmentNumber: z.string(),
});

const orderReferenceSchema = z.object({
  id: z.string(),
  key: z.string(),
  number: z.string(),
});

const carrierTrackingSchema = z.object({
  carrier: shippingCarrierSchema.nullable().catch(null),
  name: z.string().nullable(),
  trackingNumber: z.string().nullable(),
});

export interface LocationAttribute {
  kind: 'location';
  location: z.infer<typeof locationReferenceSchema>;
}

const locationAttributeSchema = locationReferenceSchema.transform(
  (location): LocationAttribute => ({ kind: 'location', location })
);

export interface ShipmentAttribute {
  kind: 'shipment';
  shipment: z.infer<typeof shipmentReferenceSchema>;
}

const shipmentAttributeSchema = shipmentReferenceSchema.transform(
  (shipment): ShipmentAttribute => ({ kind: 'shipment', shipment })
);

export interface ProjectAttribute {
  kind: 'project';
  project: z.infer<typeof projectReferenceSchema>;
}

const projectAttributeSchema = projectReferenceSchema.transform(
  (project): ProjectAttribute => ({ kind: 'project', project })
);

export interface CustomerAttribute {
  kind: 'customer';
  customer: z.infer<typeof customerReferenceSchema>;
}

const customerAttributeSchema = customerReferenceSchema.transform(
  (customer): CustomerAttribute => ({ kind: 'customer', customer })
);

const shipmentListEntrySchema = z.object({
  shipment: shipmentReferenceSchema,
  isCancelled: z.boolean(),
});

export interface ShipmentsAttribute {
  kind: 'shipments';
  entries: z.infer<typeof shipmentListEntrySchema>[];
}

const shipmentsAttributeSchema = toArraySchema(
  shipmentListEntrySchema
).transform((entries): ShipmentsAttribute => ({ kind: 'shipments', entries }));

// The three orders arrive in one shape, so which kind an attribute is comes
// from the context key it sits in rather than anything on the wire.
type OrderReference = z.infer<typeof orderReferenceSchema>;

export interface FulfillmentOrderAttribute {
  kind: 'fulfillmentOrder';
  order: OrderReference;
}

const fulfillmentOrderAttributeSchema = orderReferenceSchema.transform(
  (order): FulfillmentOrderAttribute => ({ kind: 'fulfillmentOrder', order })
);

export interface PurchaseOrderAttribute {
  kind: 'purchaseOrder';
  order: OrderReference;
}

const purchaseOrderAttributeSchema = orderReferenceSchema.transform(
  (order): PurchaseOrderAttribute => ({ kind: 'purchaseOrder', order })
);

export interface ReturnOrderAttribute {
  kind: 'returnOrder';
  order: OrderReference;
}

const returnOrderAttributeSchema = orderReferenceSchema.transform(
  (order): ReturnOrderAttribute => ({ kind: 'returnOrder', order })
);

// An instant carries the zone it was recorded in, which is the zone it reads in
export interface InstantAttribute {
  kind: 'instant';
  at: DateTime;
  timezone: string;
}

const instantAttributeSchema = z
  .object({ at: dateTimeSchema, timezone: z.string() })
  .transform((instant): InstantAttribute => ({ kind: 'instant', ...instant }));

// A day somebody typed: no clock, and no zone to be shifted into
export interface DayAttribute {
  kind: 'day';
  on: DateTime;
}

const dayAttributeSchema = dateSchema.transform(
  (on): DayAttribute => ({ kind: 'day', on })
);

export interface CarrierAttribute
  extends z.infer<typeof carrierTrackingSchema> {
  kind: 'carrier';
}

const carrierAttributeSchema = carrierTrackingSchema.transform(
  (carrier): CarrierAttribute => ({ kind: 'carrier', ...carrier })
);

const lineItemSchema = z.object({ quantity: z.string(), name: z.string() });

export interface ItemsAttribute {
  kind: 'items';
  items: z.infer<typeof lineItemSchema>[];
}

const itemsAttributeSchema = toArraySchema(lineItemSchema).transform(
  (items): ItemsAttribute => ({ kind: 'items', items })
);

export interface SourceAttribute {
  kind: 'source';
  type: string;
  name: string;
}

const sourceAttributeSchema = z
  .object({ type: z.string(), name: z.string() })
  .transform((source): SourceAttribute => ({ kind: 'source', ...source }));

export type ContextAttribute =
  | string
  | LocationAttribute
  | ShipmentAttribute
  | ProjectAttribute
  | CustomerAttribute
  | ShipmentsAttribute
  | FulfillmentOrderAttribute
  | PurchaseOrderAttribute
  | ReturnOrderAttribute
  | InstantAttribute
  | DayAttribute
  | CarrierAttribute
  | ItemsAttribute
  | SourceAttribute;

// A message says nothing in a key it has nothing for, which is how a cell knows
// there is no order to name.
export type MessageContext = Record<string, ContextAttribute | null>;

// Every shipment message says the same things about its shipment, so the five
// types share one context rather than five copies of it.
export const shipmentContextSchema = z.object({
  summary: z.string(),
  direction: z.string(),
  verb: z.string(),
  shipment: shipmentAttributeSchema,
  status: z.string(),
  from: locationAttributeSchema.nullable(),
  to: locationAttributeSchema.nullable(),
  fulfillmentOrder: fulfillmentOrderAttributeSchema.nullable(),
  purchaseOrder: purchaseOrderAttributeSchema.nullable(),
  returnOrder: returnOrderAttributeSchema.nullable(),
  carrier: carrierAttributeSchema.nullable(),
  milestoneLabel: z.string(),
  milestoneAt: instantAttributeSchema,
  expectedShipOn: dayAttributeSchema.nullable(),
  expectedDeliverOn: dayAttributeSchema.nullable(),
  shippedAt: instantAttributeSchema.nullable(),
  deliveredAt: instantAttributeSchema.nullable(),
  receivedBy: z.string().nullable(),
  receiptNote: z.string().nullable(),
  deliveryNote: z.string().nullable(),
  lineItems: itemsAttributeSchema,
});

export type ShipmentContext = z.infer<typeof shipmentContextSchema>;

export const fulfillmentOrderContextSchema = z.object({
  source: sourceAttributeSchema.nullable().default(null),
  summary: z.string(),
  variant: z.string(),
  order: fulfillmentOrderAttributeSchema,
  status: z.string(),
  project: projectAttributeSchema.nullable(),
  customer: customerAttributeSchema.nullable(),
  from: locationAttributeSchema.nullable(),
  to: locationAttributeSchema.nullable(),
  contact: z.string().nullable(),
  thirdParty: z.string().nullable(),
  shipments: shipmentsAttributeSchema,
  milestoneLabel: z.string(),
  milestoneAt: instantAttributeSchema.nullable(),
  measuredAgainst: z
    .union([dayAttributeSchema, instantAttributeSchema])
    .nullable()
    .default(null),
  promiseShipOn: dayAttributeSchema.nullable(),
  promiseDeliverOn: dayAttributeSchema.nullable(),
  shippedAt: instantAttributeSchema.nullable(),
  deliveredAt: instantAttributeSchema.nullable(),
  cancelledAt: instantAttributeSchema.nullable(),
  cancelledBy: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  lineItems: itemsAttributeSchema,
});

export type FulfillmentOrderContext = z.infer<
  typeof fulfillmentOrderContextSchema
>;

const purchaseOrderContextSchema = z.object({
  summary: z.string(),
  variant: z.string(),
  order: purchaseOrderAttributeSchema,
  status: z.string(),
  supplier: customerAttributeSchema.nullable(),
  externalPo: z.string().nullable(),
  from: locationAttributeSchema.nullable(),
  to: locationAttributeSchema.nullable(),
  thirdParty: z.string().nullable(),
  shipments: shipmentsAttributeSchema,
  milestoneLabel: z.string(),
  milestoneAt: instantAttributeSchema.nullable(),
  measuredAgainst: z
    .union([dayAttributeSchema, instantAttributeSchema])
    .nullable()
    .default(null),
  promiseShipOn: dayAttributeSchema.nullable(),
  promiseDeliverOn: dayAttributeSchema.nullable(),
  shippedAt: instantAttributeSchema.nullable(),
  deliveredAt: instantAttributeSchema.nullable(),
  receivedAt: instantAttributeSchema.nullable(),
  cancelledAt: instantAttributeSchema.nullable(),
  cancelledBy: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  lineItems: itemsAttributeSchema,
  source: sourceAttributeSchema.nullable(),
});

const returnOrderContextSchema = z.object({
  summary: z.string(),
  variant: z.string(),
  order: returnOrderAttributeSchema,
  status: z.string(),
  returnType: z.string().nullable(),
  returnReason: z.string().nullable(),
  from: locationAttributeSchema.nullable(),
  to: locationAttributeSchema.nullable(),
  thirdParty: z.string().nullable(),
  shipments: shipmentsAttributeSchema,
  milestoneLabel: z.string(),
  milestoneAt: instantAttributeSchema.nullable(),
  measuredAgainst: z
    .union([dayAttributeSchema, instantAttributeSchema])
    .nullable()
    .default(null),
  promiseShipOn: dayAttributeSchema.nullable(),
  promiseDeliverOn: dayAttributeSchema.nullable(),
  shippedAt: instantAttributeSchema.nullable(),
  deliveredAt: instantAttributeSchema.nullable(),
  receivedAt: instantAttributeSchema.nullable(),
  cancelledAt: instantAttributeSchema.nullable(),
  cancelledBy: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  lineItems: itemsAttributeSchema,
  source: sourceAttributeSchema.nullable(),
});

// Every shipment message carries the same context, so the type is a set rather
// than a branch per member.
const shipmentMessageTypeSchema = z.enum([
  'INBOUND_SHIPMENT_SHIPPED',
  'INBOUND_SHIPMENT_DELIVERED',
  'INBOUND_SHIPMENT_RECEIVED',
  'OUTBOUND_SHIPMENT_SHIPPED',
  'OUTBOUND_SHIPMENT_DELIVERED',
]);

export const fulfillmentOrderMessageTypeSchema = z.enum([
  'FULFILLMENT_ORDER_SHIPPED',
  'FULFILLMENT_ORDER_DELIVERED',
  'FULFILLMENT_ORDER_CANCELLED',
]);

export const purchaseOrderMessageTypeSchema = z.enum([
  'PURCHASE_ORDER_SHIPPED',
  'PURCHASE_ORDER_DELIVERED',
  'PURCHASE_ORDER_RECEIVED',
  'PURCHASE_ORDER_CANCELLED',
]);

export const returnOrderMessageTypeSchema = z.enum([
  'RETURN_ORDER_SHIPPED',
  'RETURN_ORDER_DELIVERED',
  'RETURN_ORDER_RECEIVED',
  'RETURN_ORDER_CANCELLED',
]);

// Match exactly: template compatibility does not establish a known shape.
const contextSchemasByFamily = {
  shipment: z.discriminatedUnion('contextVersion', [
    z.object({ contextVersion: z.literal(1), context: shipmentContextSchema }),
  ]),
  fulfillmentOrder: z.discriminatedUnion('contextVersion', [
    z.object({
      contextVersion: z.literal(1),
      context: fulfillmentOrderContextSchema,
    }),
  ]),
  purchaseOrder: z.discriminatedUnion('contextVersion', [
    z.object({
      contextVersion: z.literal(1),
      context: purchaseOrderContextSchema,
    }),
  ]),
  returnOrder: z.discriminatedUnion('contextVersion', [
    z.object({
      contextVersion: z.literal(1),
      context: returnOrderContextSchema,
    }),
  ]),
};

const messageContextSchema = z.union([
  z
    .object({ messageType: shipmentMessageTypeSchema })
    .and(contextSchemasByFamily.shipment),
  z
    .object({ messageType: fulfillmentOrderMessageTypeSchema })
    .and(contextSchemasByFamily.fulfillmentOrder),
  z
    .object({ messageType: purchaseOrderMessageTypeSchema })
    .and(contextSchemasByFamily.purchaseOrder),
  z
    .object({ messageType: returnOrderMessageTypeSchema })
    .and(contextSchemasByFamily.returnOrder),
]);

// A cell names one context attribute and labels it. Prose fields interpolate
// {key}; the key field is a reference, not a slot.
const templateCellSchema = z.object({
  label: z.string(),
  key: z.string(),
  note: z.string().optional(),
  // Names the attribute this cell's value is measured against. The screen works
  // the difference out, so it reads in the value's own zone rather than in
  // whichever one wrote the message
  offsetFrom: z.string().optional(),
  // How a cell that is text rather than a value is set, such as the heading
  // over a column. Unset reads as body text
  variant: z.enum(['SUBTITLE2']).optional(),
  // Draws a moment as the day it happened, leaving the clock to the tooltip
  isDateOnly: z.boolean().optional(),
  maxEntries: z.number().int().nonnegative().optional(),
});

export type TemplateCell = z.infer<typeof templateCellSchema>;

// Columns are read top to bottom and drawn side by side; details are the
// full-width cells beneath them, for the values too long to sit in a column.
export const bodyTemplateSchema = z.object({
  summary: z.string(),
  columns: toArraySchema(toArraySchema(templateCellSchema)),
  details: toArraySchema(templateCellSchema),
});

export type BodyTemplate = z.infer<typeof bodyTemplateSchema>;

// What the screen draws, built here rather than parsed.
export interface DrawnCell {
  key: string;
  label: string;
  // True when the cell is its label rather than a value, such as a column
  // heading, so it draws as text with no value beside it
  isText: boolean;
  variant: TemplateCell['variant'];
  maxEntries?: number;
  // True when the moment draws as its day, with the clock in the tooltip
  isDateOnly: boolean;
  // Note is the parenthetical beside the value: what the template said, or how
  // far the value was off what it was measured against
  note: string;
  // What the message says here
  value: ContextAttribute | null;
}

export interface MessageBody {
  summary: string;
  // A column with no cells left draws as the blank space that keeps the columns
  // beside it where they are
  columns: DrawnCell[][];
  details: DrawnCell[];
}

// Everything a message carries whatever its type.
const inboxEntryFieldsSchema = z.object({
  contextVersion: z.number().int(),
  key: z.string(),
  titleTemplate: z.string(),
  // Absent from a sparse listing, which the bell asks for
  bodyTemplate: bodyTemplateSchema.optional(),
  entityType: z.string(),
  entityId: z.string(),
  createdAt: dateTimeSchema,
  // Null while the message is unread
  readAt: dateTimeSchema.nullable(),
});

type InboxEntryFields = z.infer<typeof inboxEntryFieldsSchema>;

// A message whose type this bundle knows, so its context is the one that type
// declares and the screen can draw it.
export type KnownInboxEntry = InboxEntryFields &
  z.infer<typeof messageContextSchema> & { isKnownType: true };

// A message whose type this bundle predates
export interface UnknownInboxEntry extends InboxEntryFields {
  isKnownType: false;
  messageType: string;
}

// Null marks a known context that failed validation
type InvalidInboxEntry = Omit<KnownInboxEntry, 'context' | 'contextVersion'> & {
  contextVersion: number;
  context: null;
};

export const inboxEntrySchema = z.union([
  inboxEntryFieldsSchema
    .and(messageContextSchema)
    .transform((entry): KnownInboxEntry => ({ ...entry, isKnownType: true })),
  inboxEntryFieldsSchema
    .extend({
      messageType: z
        .string()
        .refine(
          (type) =>
            !shipmentMessageTypeSchema.safeParse(type).success &&
            !fulfillmentOrderMessageTypeSchema.safeParse(type).success &&
            !purchaseOrderMessageTypeSchema.safeParse(type).success &&
            !returnOrderMessageTypeSchema.safeParse(type).success
        ),
    })
    .transform(
      (entry): UnknownInboxEntry => ({ ...entry, isKnownType: false })
    ),
  inboxEntryFieldsSchema
    .extend({
      messageType: z.union([
        shipmentMessageTypeSchema,
        fulfillmentOrderMessageTypeSchema,
        purchaseOrderMessageTypeSchema,
        returnOrderMessageTypeSchema,
      ]),
    })
    .transform(
      (entry): InvalidInboxEntry => ({
        ...entry,
        isKnownType: true,
        context: null,
      })
    ),
]);

export type InboxEntry =
  | KnownInboxEntry
  | UnknownInboxEntry
  | InvalidInboxEntry;

export const inboxListingSchema = z.object({
  results: toArraySchema(inboxEntrySchema),
  totalCount: z.number(),
});

export type InboxListing = z.infer<typeof inboxListingSchema>;

export const inboxCountSchema = z.object({
  unreadCount: z.number(),
});

export type InboxCount = z.infer<typeof inboxCountSchema>;

export const messageTypeCatalogEntrySchema = z.object({
  messageType: z.string(),
  group: z.string(),
  label: z.string(),
  description: z.string(),
  channels: toArraySchema(messageChannelSchema),
});

export type MessageTypeCatalogEntry = z.infer<
  typeof messageTypeCatalogEntrySchema
>;

export const messageTypeCatalogSchema = z.object({
  results: toArraySchema(messageTypeCatalogEntrySchema),
});

export const typeSubscriptionSchema = z.object({
  messageType: z.string(),
  channels: toArraySchema(messageChannelSchema),
});

export const watchedEntitySchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
});

export const entityWatchSchema = z.object({
  entityType: z.string(),
  entityId: z.string(),
  // Null once the record it points at no longer resolves. The id above is what
  // stops the watch either way
  entity: watchedEntitySchema.nullable(),
  channels: toArraySchema(messageChannelSchema),
  watchingSince: dateTimeSchema,
});

export type EntityWatch = z.infer<typeof entityWatchSchema>;

export const subscriptionsSchema = z.object({
  typeSubscriptions: toArraySchema(typeSubscriptionSchema),
  entityWatches: toArraySchema(entityWatchSchema),
});

export type Subscriptions = z.infer<typeof subscriptionsSchema>;
